import { useCallback, useRef } from "react";
import { toast } from "sonner";
import { createSticker, updateSticker, deleteSticker } from "@/lib/api";

export const STICKER_PREFIX = "sticker::";
export const isStickerNode = (n) => n?.type === "sticker";

export function useMapStickers(setNodes) {
  const rotations = useRef({});

  const patchNodeData = useCallback(
    (sid, patch) =>
      setNodes((ns) =>
        ns.map((n) => (n.id === STICKER_PREFIX + sid ? { ...n, data: { ...n.data, ...patch } } : n))
      ),
    [setNodes]
  );

  const onRotate = useCallback(
    (sid, delta) => {
      const rotation = (((rotations.current[sid] || 0) + delta) % 360 + 360) % 360;
      rotations.current[sid] = rotation;
      patchNodeData(sid, { rotation });
      updateSticker(sid, { rotation }).catch(() => toast.error("Couldn't save sticker"));
    },
    [patchNodeData]
  );

  const onResize = useCallback((sid, p) => {
    const size = Math.round(Math.min(400, Math.max(24, p.width)));
    updateSticker(sid, { size, x: p.x, y: p.y }).catch(() => toast.error("Couldn't save sticker"));
  }, []);

  const onDelete = useCallback(
    (sid) => {
      setNodes((ns) => ns.filter((n) => n.id !== STICKER_PREFIX + sid));
      deleteSticker(sid).catch(() => toast.error("Couldn't remove sticker"));
    },
    [setNodes]
  );

  const toNode = useCallback(
    (st) => {
      rotations.current[st.id] = st.rotation || 0;
      return {
        id: STICKER_PREFIX + st.id,
        type: "sticker",
        position: { x: st.x, y: st.y },
        style: { width: st.size, height: st.size },
        data: { stickerId: st.id, kind: st.kind, rotation: st.rotation || 0, onRotate, onResize, onDelete },
      };
    },
    [onRotate, onResize, onDelete]
  );

  const addSticker = useCallback(
    async (kind, position) => {
      try {
        const st = await createSticker({ kind, x: position.x - 40, y: position.y - 40, size: 80, rotation: 0 });
        setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { ...toNode(st), selected: true }]);
      } catch {
        toast.error("Couldn't add sticker");
      }
    },
    [setNodes, toNode]
  );

  const saveStickerPosition = useCallback((node) => {
    updateSticker(node.data.stickerId, { x: node.position.x, y: node.position.y }).catch(() => {});
  }, []);

  const removeSticker = useCallback((sid) => {
    deleteSticker(sid).catch(() => toast.error("Couldn't remove sticker"));
  }, []);

  return { toNode, addSticker, saveStickerPosition, removeSticker };
}
